import React, { useState, useEffect, useMemo, ChangeEvent, MouseEvent } from 'react';
import ListWithDetails from '../../Common/ListWithDetails';
import {
  AIModel,
  AITool,
  Patient,
  Prompt,
  PromptLog,
  PromptRequest,
  SavedPromptRequestField,
} from '../../../generated/api';
import GenericBadge from '../../Utils/GenericBadge';
import { api } from '../../../careand-redux/api/ApiThunks';
import { useAppDispatch } from '../../../careand-redux/useAppDispatch';
import DefaultButton from '../../Utils/NewDefaultButton';
import PerformanceUtils from '../../../services/PerformanceUtils';
import DefaultTextField from '../../Utils/DefaultTextField';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultSelectField from '../../Utils/DefaultSelectField';
import _ from 'lodash';
import './style.scss';
import useAssistant from '../../../hooks/useAssistant';
import IconButton from '@material-ui/core/IconButton';
import Icon from '../../Utils/NewIcon';
import { useIsLoading } from '../../../careand-redux/slices/ui/hooks';
import PatientSearchField from '../../Patient/PatientSearchField';
import { DefaultCheckbox, DefaultSwitch } from '../../../services/UIUtils';
import DefaultModal from '../../Utils/DefaultModal';
import { useAppSelector } from '../../../careand-redux/store';
import StaffUtils from '../../../services/StaffUtils';
import CalendarUtils from '../../../services/CalendarUtils';
import useFetchPatients from '../../../hooks/useFetchPatients';
import PatientUtils from '../../../services/PatientUtils';
import AIUtils from '../../../services/AIUtils';
import ErrorBoundary from '../../Utils/ErrorBoundary';
import { UncontrolledCollapse } from '../../Utils/Collapse';
import MiscUtils from '../../../services/MiscUtils';

const PromptDashboard = () => {
  const dispatch = useAppDispatch();
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const [tools, setTools] = useState<AITool[]>([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(api.AI.getModels()).then((response) => {
      if (response) setModels(response);
    });
  }, [dispatch]);

  useEffect(() => {
    dispatch(api.AI.getTools()).then((response) => {
      if (response) setTools(response);
    });
  }, [dispatch]);

  useEffect(() => {
    dispatch(api.AI.getPrompts()).then((response) => {
      if (response) setPrompts(_.sortBy(response, 'name'));
    });
  }, [dispatch]);

  function onPromptResult(prompt: Prompt | undefined) {
    if (prompt) {
      setPrompts((prevState) => [...PerformanceUtils.editOrAdd(prompt, prevState)]);
    }
  }

  const filteredPrompts = useMemo(() => {
    return prompts.filter((prompt) => prompt.name.toLowerCase().includes(filter.toLowerCase()));
  }, [prompts, filter]);

  return (
    <div className="prompt-dashboard box full-width full-height">
      <ListWithDetails
        items={filteredPrompts}
        addButton={
          <div style={{ margin: '0 20px' }}>
            <DefaultTextField
              label="Filter"
              style={{ width: '100%' }}
              name="name"
              value={filter}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value)}
            />
          </div>
        }
        renderBadge={(prompt) => (
          <GenericBadge title={prompt.name} info={<span>{prompt.description}</span>} />
        )}
        renderDetails={(prompt) => (
          <PromptDetails
            key={prompt.id || 'new'}
            prompt={prompt}
            models={models}
            tools={tools}
            onSave={onPromptResult}
          />
        )}
      />
    </div>
  );
};

interface PromptDetailsProps {
  prompt: Prompt | undefined;
  onSave?: (prompt: Prompt) => void;
  models: AIModel[];
  tools?: AITool[];
  fields?: SavedPromptRequestField[];
  patientId?: string;
  documentId?: string;
  response?: string;
  thinking?: string;
}

const PromptDetails: React.FC<PromptDetailsProps> = ({
  prompt,
  onSave,
  models,
  tools,
  fields,
  patientId,
  documentId,
  response,
  thinking,
}) => {
  const dispatch = useAppDispatch();
  const [editedPrompt, setEditedPrompt] = useState<Prompt | undefined>(prompt);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [testPrompt, setTestPrompt] = useState<boolean>(!!response);

  const allowedTools = useMemo(() => {
    return tools?.filter((it) => !!editedPrompt?.allowed_tools?.includes(it.name)) ?? [];
  }, [tools, editedPrompt]);

  const providers = useMemo(() => {
    return [' ', ..._.uniqBy(models, 'provider').map(({ provider }) => provider)];
  }, [models]);

  useEffect(() => {
    setEditedPrompt(prompt);
  }, [prompt]);

  if (!editedPrompt) return null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setEditedPrompt((prev) => ({ ...prev!, [name]: value }));
  };

  function onToolToggle(tool: string) {
    setEditedPrompt((prev) => {
      let allowed_tools = prev?.allowed_tools ?? [];
      if (allowed_tools.includes(tool)) {
        allowed_tools = allowed_tools.filter((it) => it !== tool);
      } else {
        allowed_tools = [...allowed_tools, tool];
      }

      return { ...prev, allowed_tools };
    });
  }

  return (
    <div className="prompt-details padded-box">
      {!!prompt && showLogs && (
        <DefaultModal
          className="modal large-modal"
          title={`${editedPrompt?.name} Logs`}
          wrap={true}
          padding={false}
          content={<PromptLogs prompt={prompt} models={models} />}
          isOpen={showLogs}
          onRequestClose={() => setShowLogs(false)}
        />
      )}
      <div className={'form' + (testPrompt ? ' test-open' : '')}>
        <span className="small-title">Prompt Details</span>
        <form onSubmit={(ev) => ev.preventDefault()}>
          <DefaultTextField
            name="name"
            label="Name"
            value={editedPrompt.name}
            onChange={handleChange}
            disabled={!!response}
          />
          <DefaultTextField
            name="description"
            label="Description"
            value={editedPrompt.description}
            onChange={handleChange}
            disabled={!!response}
          />
          <div className="flex flex-wrap gap-2">
            <DefaultSelectField
              name="provider"
              label="Provider"
              className="flex-grow"
              value={editedPrompt.provider}
              inputs={providers}
              disabled
            />
            {editedPrompt.provider && (
              <DefaultSelectField
                name="model"
                label="Model"
                className="flex-grow"
                value={editedPrompt.model}
                disabled
                inputs={[
                  ' ',
                  ...models
                    .filter((model) => model.provider === editedPrompt.provider)
                    .map(({ model }) => model)
                    .sort(),
                ]}
              />
            )}
            <DefaultTextField
              name="budget_tokens"
              label="Budget Tokens"
              className="flex-grow"
              style={{ width: 'auto' }}
              value={'' + (editedPrompt.budget_tokens || 0)}
              disabled
            />
          </div>

          {allowedTools.length > 0 && (
            <>
              <div style={{ color: '#b4b4b4', fontWeight: 100 }}>Allowed Tools</div>
              <table className="tool-selector">
                {allowedTools.map((tool) => {
                  return (
                    <tr className="tool" key={tool.name}>
                      <td className="tool-name">{tool.name}</td>
                      <td className="tool-description">{tool.description}</td>
                    </tr>
                  );
                })}
              </table>
            </>
          )}
          <label>Context</label>
          <TextareaAutosize
            name="context"
            value={editedPrompt.context}
            onChange={handleChange}
            minRows={3}
            style={{ width: '100%' }}
            disabled={!testPrompt}
          />
          <label>Prompt</label>
          <TextareaAutosize
            name="prompt"
            value={editedPrompt.prompt}
            onChange={handleChange}
            minRows={3}
            style={{ width: '100%' }}
            disabled={!testPrompt}
          />
          <div className="flex flex-wrap gap-2">
            <DefaultSwitch
              label="Include Main Context"
              padded={false}
              checked={!!editedPrompt.include_main_context}
              disabled
            />
            <DefaultSwitch
              label="Requires Patient"
              padded={false}
              checked={!!editedPrompt.requires_patient}
              disabled
            />
            <DefaultSwitch
              label="Requires Document"
              padded={false}
              checked={!!editedPrompt.requires_document}
              disabled
            />
            <DefaultSwitch
              label="Requires Template"
              padded={false}
              checked={!!editedPrompt.requires_template}
              disabled
            />
          </div>
          <div className="row gap-2 mb-3">
            <DefaultButton
              name="Test Prompt"
              icon="terminal"
              onClick={() => setTestPrompt((prevState) => !prevState)}
            />
            <DefaultButton
              name="Show Logs"
              icon="rectangle-history"
              onClick={() => setShowLogs(true)}
              hide={!editedPrompt.id || !!fields}
            />
          </div>
        </form>
      </div>
      {testPrompt && (
        <TestPrompt
          prompt={editedPrompt}
          models={models}
          initialValues={{ fields, patientId, documentId, response, thinking }}
        />
      )}
    </div>
  );
};

interface InitialValues {
  fields?: SavedPromptRequestField[];
  patientId?: string;
  documentId?: string;
  templateId?: string;
  response?: string;
  thinking?: string;
}

const TestPrompt: React.FC<{
  prompt: Prompt;
  models: AIModel[];
  initialValues?: InitialValues;
}> = ({ prompt, models, initialValues }) => {
  const dispatch = useAppDispatch();

  const initialPatientId = initialValues?.patientId;
  const initialDocumentId = initialValues?.documentId;
  const initialTemplateId = initialValues?.templateId;
  const initialFields = initialValues?.fields;
  const initialResponse = initialValues?.response;

  const [documentId, setDocumentId] = useState<string>(initialDocumentId || '');
  const [templateId, setTemplateId] = useState<string>(initialTemplateId || '');
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [testSetups, setTestSetups] = useState<AIModel[]>([
    {
      provider: prompt.provider,
      model: prompt.model,
    },
  ]);
  const [shouldRequest, setShouldRequest] = useState(false);

  useEffect(() => {
    if (initialFields?.length! > 0) {
      let fieldValues: Record<string, string> = {};
      initialFields?.forEach(({ name, value }) => {
        fieldValues[name] = value!;
      });
      setFieldValues(fieldValues);
    }
  }, [initialFields]);

  useEffect(() => {
    if (initialPatientId) {
      dispatch(api.Patients.getPatient({ id: initialPatientId })).then(setPatient);
    }
  }, [dispatch, initialPatientId]);

  useEffect(() => {
    setTestSetups((prevState) => {
      if (prevState.length === 0) {
        prevState = [];
      }
      prevState[0] = {
        provider: prompt.provider,
        model: prompt.model,
      };
      return [...prevState];
    });
  }, [prompt]);

  const fields = useMemo(() => {
    if (!prompt.prompt) return [];
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = prompt.prompt.match(regex);
    if (!matches) return [];
    let result = [...new Set(matches.map((match) => match.slice(2, -2)))];
    return result.filter((field) => field !== 'HR');
  }, [prompt.prompt]);

  async function onTestPrompt() {
    setShouldRequest(true);
    setTimeout(() => {
      setShouldRequest(false);
    }, 100);
  }

  function onRemove(index: number) {
    setTestSetups((prevSetups) => {
      const newSetups = [...prevSetups];
      newSetups.splice(index, 1);
      return newSetups;
    });
  }

  function onAdd() {
    setTestSetups((prevState) => {
      return [...prevState, { provider: prompt.provider, model: prompt.model }];
    });
  }

  const handleFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const loadingReply = useIsLoading(api.AI.prompt2);

  const patientId = patient?.id;

  const convertedFieldValues = useMemo<SavedPromptRequestField[]>(() => {
    return Object.entries(fieldValues).map(([name, value]) => ({
      name,
      value,
    }));
  }, [fieldValues]);

  return (
    <div className="test-prompt">
      <span className="small-title">Test Prompt</span>
      {prompt.requires_patient && (
        <PatientSearchField onSelect={setPatient} patient={patient} hideTitle={true} />
      )}
      {prompt.requires_document && (
        <DefaultTextField
          key="document_id"
          label="Document ID"
          style={{ width: '100%' }}
          name="document_id"
          value={documentId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setDocumentId(event.target.value)}
        />
      )}
      {prompt.requires_template && (
        <DefaultTextField
          key="template_id"
          label="Template ID"
          style={{ width: '100%' }}
          name="template_id"
          value={documentId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setTemplateId(event.target.value)}
        />
      )}
      {fields.map((name) => (
        <>
          <label>{name}</label>
          <TextareaAutosize
            name={name}
            value={fieldValues[name] || ''}
            onChange={handleFieldChange}
            minRows={1}
            style={{ width: '100%' }}
          />
        </>
      ))}
      <div className="button-wrapper flex gap-2">
        <DefaultButton
          name="Test Prompt"
          className="blue"
          loading={loadingReply}
          onClick={onTestPrompt}
        />
        <DefaultButton
          name="Copy PromptLog"
          className="white"
          onClick={() => {
            const text = [
              `<promptName>${prompt.name}</promptName>`,
              `<promptDescription>${prompt.description}</promptDescription>`,
              `<context>${prompt.context}</context>`,
              `<prompt>${prompt.prompt}</prompt>`,
              `<llmResponse>${initialResponse}</llmResponse>`,
            ].join('\n\n');
            window.navigator.clipboard.writeText(text).then(() => {
              MiscUtils.showToastrInfo({ title: 'PromptLog copied to clipboard ' });
            });
          }}
        />
      </div>
      <div className="results">
        {initialResponse && (
          <TestPromptResults
            aiPrompt={prompt}
            aiModel={{ model: prompt.model, provider: prompt.provider }}
            allowRemove={false}
            models={models}
            initialResponse={initialResponse}
            shouldRequest={false}
            initialThinking={initialValues?.thinking}
          />
        )}
        {testSetups?.map((setup, index) => {
          return (
            <TestPromptResults
              key={JSON.stringify(setup) + index}
              aiPrompt={prompt}
              aiModel={setup}
              onRemove={() => onRemove(index)}
              allowRemove={index > 0}
              shouldRequest={shouldRequest}
              index={index}
              models={models}
              patientId={patientId}
              fields={convertedFieldValues}
              documentId={documentId}
            />
          );
        })}
        <div className="add">
          <IconButton onClick={onAdd}>
            <Icon src="plus" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

function Thinking({ thinking }: { thinking: string | undefined }) {
  return (
    <>
      <div className="small-title">Thinking</div>
      <div className="thinking">
        <pre>{thinking}</pre>
      </div>
    </>
  );
}

function ToolUsage({ response }: { response?: string }) {
  const usage = useMemo(() => {
    const sections: any[] = [];
    if (!response) {
      return sections;
    }

    for (const json of AIUtils.generateJSONSections(response)) {
      if (json?.type === 'tool_use' || json?.type === 'tool_result') {
        sections.push(json);
      }
    }
    return sections;
  }, [response]);

  const [hoverId, setHoverId] = useState<string | undefined>();

  function scrollToMatch(type: string, id: string) {
    type = type === 'tool_use' ? 'tool_result' : 'tool_use';
    const element = document.querySelector(`[data-type="${type}"][data-id="${id}"]`);
    element?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  function scrollToNext(ev: MouseEvent<HTMLElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    const target = (ev.currentTarget as HTMLDivElement).parentElement?.parentElement?.parentElement
      ?.parentElement as HTMLDivElement;
    const selector = `[data-id="${target.getAttribute(
      'data-id',
    )}"][data-type="${target.getAttribute('data-type')}"] + *`;
    const nextElement = document.querySelector(selector);
    nextElement?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  return (
    <div className="tool-usage" onMouseLeave={() => setHoverId(undefined)}>
      {usage.map((json) => {
        if (json.type === 'tool_use') {
          return (
            <div
              className="tool-use"
              key={`tool-use-${json.id}`}
              data-type="tool_use"
              data-id={json.id}
              onMouseOver={() => setHoverId(json.id)}
              data-hover={hoverId === json.id}
            >
              <span className="small-title flex gap-1">
                Tool Use - {json.name}
                <span>({json.timestamp}s)</span>
                <Icon
                  className="jump-arrow"
                  style={{ rotate: '90deg' }}
                  alt="Go to result"
                  showTooltip
                  src="arrow-right"
                  onClick={() => scrollToMatch('tool_use', json.id)}
                />
                <Icon
                  className="jump-arrow"
                  src="arrow-right"
                  alt="Go to next entry"
                  showTooltip
                  onClick={scrollToNext}
                />
              </span>
              <UncontrolledCollapse title="Input">
                <div className="tool-use-params">
                  <pre>{JSON.stringify(json.input, null, 2)}</pre>
                </div>
              </UncontrolledCollapse>
            </div>
          );
        }
        return (
          <div
            className="tool-result"
            key={`tool-result-${json.tool_use_id}`}
            data-type="tool_result"
            data-id={json.tool_use_id}
            onMouseOver={() => setHoverId(json.tool_use_id)}
            data-hover={hoverId === json.tool_use_id}
          >
            <span className="small-title flex gap-1">
              Tool Result
              <span>
                [took {json.time}s] ({json.timestamp}s)
              </span>
              <Icon
                className="jump-arrow"
                style={{ rotate: '-90deg' }}
                src="arrow-right"
                alt="Go to ToolUse"
                showTooltip
                onClick={() => scrollToMatch('tool_result', json.tool_use_id)}
              />
              <Icon
                className="jump-arrow"
                src="arrow-right"
                alt="Go to next entry"
                showTooltip
                onClick={scrollToNext}
              />
            </span>
            <UncontrolledCollapse title="Result">
              <div className="tool-use-params">
                <ErrorBoundary content={<pre>{json.content}</pre>}>
                  <pre>
                    {(() => {
                      const result = tryParse(json.content);
                      if (result.failed_to_parse) {
                        return result.failed_to_parse;
                      }
                      return JSON.stringify(result, null, 2);
                    })()}
                  </pre>
                </ErrorBoundary>
              </div>
            </UncontrolledCollapse>
          </div>
        );
      })}
    </div>
  );
}

function tryParse(value: string) {
  try {
    return JSON.parse(value);
  } catch (err) {
    return {
      failed_to_parse: value,
    };
  }
}

const TestPromptResults: React.FC<{
  aiPrompt: Prompt;
  aiModel: AIModel;
  onRemove?: () => void;
  allowRemove: boolean;
  shouldRequest?: boolean;
  index?: number;
  models: AIModel[];
  patientId?: string;
  fields?: SavedPromptRequestField[];
  documentId?: string;
  initialResponse?: string;
  initialThinking?: string;
}> = ({
  aiPrompt,
  aiModel,
  onRemove,
  allowRemove,
  shouldRequest,
  index,
  models,
  patientId,
  fields,
  documentId,
  initialResponse,
  initialThinking,
}) => {
  const [response, setResponse] = useState<string | undefined>(initialResponse);
  const [time, setTime] = useState<string>('');
  const [modelInfo, setModelInfo] = useState<AIModel>({
    ...aiModel,
    budget_tokens: aiPrompt.budget_tokens,
  });
  const streamingKey = `PromptTest.${modelInfo.provider}.${modelInfo.model}.${index}`;
  const { streamingText, prompt2 } = useAssistant(streamingKey);

  const providers = useMemo(() => {
    return _.uniqBy(models, 'provider').map(({ provider }) => provider);
  }, [models]);

  useEffect(() => {
    const execute = async () => {
      setResponse(undefined);
      setTime('');
      let date = new Date();
      let response = await prompt2(
        {
          name: aiPrompt.name,
          prompt: aiPrompt.prompt!,
          context: aiPrompt.context,
          include_main_context: aiPrompt.include_main_context,
          provider: modelInfo.provider,
          model: modelInfo.model,
          allowed_tools: aiPrompt.allowed_tools,
        },
        patientId,
        fields,
        documentId,
        modelInfo.budget_tokens,
      );
      //@ts-expect-error is this valid?
      let time = new Date() - date;
      setTime(`${time} ms`);
      setResponse(response);
    };

    if (shouldRequest) {
      execute();
    }
  }, [shouldRequest, fields, aiPrompt, patientId, modelInfo, prompt2]);

  return (
    <div className="result">
      <div className="row">
        <DefaultSelectField
          name="provider"
          label="Provider"
          value={modelInfo.provider}
          disabled={!!initialResponse}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setModelInfo((prevState) => ({
              ...prevState,
              provider: event.target.value,
              model: '',
            }));
          }}
          inputs={providers}
          style={{ width: '35%' }}
        />
        {modelInfo.provider && (
          <DefaultSelectField
            name="model"
            label="Model"
            value={modelInfo.model}
            disabled={!!initialResponse}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setModelInfo((prevState) => ({
                ...prevState,
                model: event.target.value,
              }));
            }}
            inputs={models
              .filter((model) => model.provider === modelInfo.provider)
              .map(({ model }) => model)
              .sort()}
            style={{ width: '45%' }}
          />
        )}
        <DefaultTextField
          name="budget_tokens"
          label="Budget Tokens"
          value={'' + (modelInfo.budget_tokens || aiPrompt.budget_tokens || 0)}
          disabled={!!initialResponse}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setModelInfo((prevState) => ({
              ...prevState,
              budget_tokens: parseInt(event.target.value),
            }));
          }}
          style={{ width: '20%' }}
        />
      </div>
      {allowRemove && onRemove && (
        <IconButton onClick={() => onRemove()} className="remove-button">
          <Icon src="trash" />
        </IconButton>
      )}
      {(streamingText || response) && (
        <>
          <span className="small-title">Response ({time})</span>
          <pre className="response">{response || streamingText}</pre>
        </>
      )}
      <div className="small-title">Tool Usage</div>
      <ToolUsage response={response} />
      <Thinking thinking={initialThinking} />
    </div>
  );
};

const PromptLogs: React.FC<{ prompt: Prompt; models: AIModel[] }> = ({ prompt, models }) => {
  const dispatch = useAppDispatch();
  const [logs, setLogs] = useState<PromptLog[]>([]);

  const promptId = prompt.id;

  const loading = useIsLoading(api.AI.getPromptLogs);

  useFetchPatients(logs.map(({ request }) => request));

  useEffect(() => {
    dispatch(api.AI.getPromptLogs({ id: promptId! })).then((response) => {
      if (response) setLogs(response);
    });
  }, [dispatch, promptId]);

  return (
    <div className="prompt-logs full-height">
      <ListWithDetails
        loading={loading}
        items={logs}
        renderBadge={(log) => <PromptLogBadge key={log.id} log={log} />}
        renderDetails={(log) => (
          <PromptLogDetails key={log.id} originalPrompt={prompt} log={log} models={models} />
        )}
      />
    </div>
  );
};

const PromptLogBadge: React.FC<{ log: PromptLog }> = ({ log }) => {
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const patients = useAppSelector((state) => state.patients.byId);

  const user = admins[log.user_id!] || physicians[log.user_id!];
  let name = StaffUtils.getName(user) || '';
  if (log.request.patient_id) {
    name =
      name.split(' ')[0] +
      ' - ' +
      PatientUtils.getName(patients[log.request.patient_id], undefined, false, false);
  }

  return (
    <GenericBadge
      title={name}
      info={
        <span>
          {CalendarUtils.format(log.date, 'DD/MM/YY h:mm A')} - {log.duration / 1000}s
        </span>
      }
    />
  );
};

const PromptLogDetails: React.FC<{
  originalPrompt: Prompt;
  log: PromptLog;
  models: AIModel[];
}> = ({ originalPrompt, log, models }) => {
  const prompt = {
    ...originalPrompt,
    id: log.prompt_id,
    prompt: log.request.prompt,
    context: log.request.context,
    include_main_context: log.request.include_main_context,
    provider: log.request.provider,
    model: log.request.model,
  };

  return (
    <PromptDetails
      prompt={prompt}
      fields={log.request.fields}
      patientId={log.request.patient_id}
      documentId={log.request.document_id}
      response={log.response}
      models={models}
      thinking={log.thinking}
    />
  );
};

export default PromptDashboard;
